import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { first, map, tap, shareReplay, switchMap } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { AssetsService } from './assets.service';
import { DocumentTypesService } from './document-types.service';
import { InitiativesService } from './initiatives.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { PerimetersService } from './perimeters.service';
import { ReferenceDataService } from './reference-data.service';
import { idMapToArray, ScopeService } from './scope.service';
import { YearsService } from "./years.service";
import { ActivatedRoute } from "@angular/router";
import { InvestmentScenariiService } from "./investment-scenarii.service";
import { SpreadsheetColumnsService } from './spreadsheet-columns.service';
import { AutoPpaService } from './auto-ppa.service';
import { RoadmapService } from './roadmap.service';
import { CellClickedEvent, ColDef } from 'ag-grid-community';
import { ExportsService } from './exports.service';
import { QuotePartService } from './quote-part.service';
import { PricesheetHeaderComponent } from '../../pages/insight/tabs/asset-types/pricesheet-header.component';
import { OpenPriceSheetsRendererComponent } from '../../pages/insight/tabs/asset-types/open-price-sheets-renderer.component';
import { PriceSheetsListDialogComponent } from '../../@theme/components';
import { NbDialogService } from '@nebular/theme';
import { PriceSheetService } from './price-sheet.service';
import { AssetTypesService } from './asset-types.service';
import { QuantityUnitsService } from './quantity-units.service';
import { ConfigService } from './config.service';
import { SettingsService } from './settings.service';
import { StateLevelsService } from './state-levels.service';

@Injectable()
export class SpreadsheetColumnsAssetsService extends SpreadsheetColumnsService {

  /**
   * store the value of options that're going to fetch with the asset table so that we can
   * access this from other services.
   * @private
   */
  private readonly currentAssetTableOptions = new BehaviorSubject<any>(null);

  constructor(
    protected documentTypesService: DocumentTypesService,
    protected investmentCategoriesService: InvestmentCategoriesService,
    protected investmentReasonsService: InvestmentReasonsService,
    protected scope: ScopeService,
    protected translate: TranslateService,
    protected api: APIService,
    protected perimetersService: PerimetersService,
    protected referenceDataService: ReferenceDataService,
    protected assetsService: AssetsService,
    protected yearsService: YearsService,
    protected route: ActivatedRoute,
    protected investmentScenariiService: InvestmentScenariiService,
    protected initiativesService: InitiativesService,
    protected roadmapService: RoadmapService,
    private autoPpaService: AutoPpaService,
    private scopeService: ScopeService,
    private exportsService: ExportsService,
    protected quotePartService: QuotePartService,
    private dialogService: NbDialogService,
    private priceSheetService: PriceSheetService,
    private assetTypesService: AssetTypesService,
    private quantityUnitsService: QuantityUnitsService,
    protected config: ConfigService,
    private settingsService: SettingsService,
    private stateLevelsService: StateLevelsService
  ) {
    super(
      documentTypesService, investmentCategoriesService, investmentReasonsService,
      scope, translate, api, perimetersService, referenceDataService, assetsService,
      yearsService, route, investmentScenariiService, initiativesService, roadmapService,
      quotePartService, config
    );
  }

  private quantityUnitMap = {};

  private agGridAssetColumns(year: number, _isAutoPpaEnabled: boolean, config: { onDialogClose: (params: any) => void }): Observable<KPI.CustomColDef[]> {
    return combineLatest([
      this.config.customerConfig$,
      this.settingsService.assetMaintenanceManagement$,
      this.settingsService.defectManagement$,
      this.settingsService.assetEquipmentValidation$
    ]).pipe(
      map(([customerConfig, maintenanceEnabled, defectManagement, equipmentValidation]) => {
    return [
      {
        field: 'row_nb',
        headerName: '',
        rowDrag: false,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        filter: false,
        resizable: false,
        sortable: false,
        menuTabs: [],
        valueGetter: this.setRowNumber,
        width: 45,
        minWidth: 45,
        maxWidth: 45,
        pinned: true,
        lockPosition: true,
        enableCellChangeFlash: false,
        cellStyle: { 'text-align': 'center', color: '#707171', 'font-weight': 'normal' },
      },
      // COLUMNS SHOWN BY DEFAULT
      {
        field: 'asset_label',
        headerName: this.translate.instant('pages.insight.assets.column_label'),
        pinned: true,
        width: 200,
      },
      ...(equipmentValidation ? [{
        field: 'missing_data_count',
        headerName: this.translate.instant('Missing data'),
        width: 140,
        filter: 'agNumberColumnFilter',
        hide: true,
        valueFormatter: (params) => {
          const value = params.value;
          return value === 0 || value === null || value === undefined ? '-' : value;
        },
        cellStyle: { 'font-weight': 'normal' },
      }] : []),
      {
        field: 'site',
        headerName: this.translate.instant('pages.insight.assets.column_site'),
        refData: this.sitesMap,
        filter: 'agSetColumnFilter',
        hide: false,
        width: 80,
        quickFilter: true,
        filterParams: {
          values: (params) => {
            // supply values to the set filter
            this.perimetersService.perimeters$.subscribe((perimeters) =>
              params.success([
                ...new Set(
                  perimeters
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((p) => p.id),
                ),
              ]),
            );
          },
        },
      },
      {
        field: 'perimeter',
        headerName: this.translate.instant('Perimeter'),
        refData: this.perimetersMap,
        filter: 'agSetColumnFilter',
        width: 120,
        hide: true,
      },
      {
        field: 'perimeter_type',
        headerName: this.translate.instant('Perimeter type'),
        hide: true,
        refData: this.perimeterTypesMap,
        filter: 'agSetColumnFilter',
        width: 130
      },
      {
        field: 'quantity',
        headerName: this.translate.instant('Qty'),
        width: 60,
        filter: 'agNumberColumnFilter',
        cellStyle: { 'font-weight': 'normal' },
      },
      {
        headerName: this.translate.instant('Qty unit'),
        field: 'quantity_unit',
        filter: 'agSetColumnFilter',
        width: 80,
        refData: this.quantityUnitMap,
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return 'u';
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || 'u',
      },
      {
        field: 'last_technical_evaluation',
        headerName: this.translate.instant('Last technical evaluation'),
        cellRenderer: 'lastTechnicalStateRenderer',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params) => {
            params.success([null, 0, 1, 2, 3]);
          },
          refreshValuesOnOpen: false,
          suppressClearModelOnRefreshValues: true,
        },
        refData: this.technicalEvaluationMap,
        cellClass: 'no-padding-cell',
        comparator: this.technicalStateComparator,
        width: 130,
        hide: false,
      },
      {
        field: 'last_audit_date',
        headerName: this.translate.instant('Evaluated on'),
        valueFormatter: this.dateFormatter,
        width: 90,
        filter: 'agDateColumnFilter',
        hide: false,
      },
      {
        field: 'installation_year',
        headerName: this.translate.instant('pages.insight.assets.column_installation_year'),
        width: 95,
        allowNull: false,
        filter: 'agNumberColumnFilter',
        hide: false,
      },
      {
        field: 'end_of_life',
        headerName: this.translate.instant("Expected end of life"),
        hide: false,
        width: 125,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'lifetime_vs_theorical',
        headerName: this.translate.instant('pages.insight.assets.column_lifetime_vs_theorical'),
        width: 120,
        filter: 'agNumberColumnFilter',
        valueFormatter: this.numberSignFormatter,
      },
      {
        field: 'price_sheets_count',
        headerName: this.translate.instant("Price sheet(s)"),
        hide: true,
        headerComponent: PricesheetHeaderComponent,
        cellRenderer: OpenPriceSheetsRendererComponent,
        cellRendererParams: {
          fromAssetTable: true
        },
        valueGetter: (params) => params.data?.price_sheets_count ?? null,
        filter: 'agNumberColumnFilter',
        sortable: true,
        width: 80,
        onCellClicked: (event: CellClickedEvent) => {
          const clickTimeout = setTimeout(() => {
            this.openPriceSheets(event.data.asset_type_object).subscribe((dialogResult) => {
              config.onDialogClose(dialogResult);
            });
          }, 150);
          (event.event.target as any).__priceSheetClickTimeout = clickTimeout;
        }
      } as any,
      ...(maintenanceEnabled ? [
        {
          field: 'maintenance_evaluation',
          headerName: this.translate.instant('asset_form_drawer.maintenance.last_evaluation'),
          width: 185,
          filter: 'agSetColumnFilter',
          hide: false,
          allowNull: true,
          filterParams: {
            values: (params) => {
              if (params && params.colDef && params.colDef.field) {
                this.subscriptions.push(
                  this.perimetersService.perimeters$
                    .pipe(
                      switchMap((perimeters) => {
                        const { api } = params;
                        return this.api.available_filter_values({
                          model: 'MaintenanceEvaluation',
                          buildings: PerimetersService.getBuildingsIds(perimeters),
                          filterModel: api.getFilterModel(),
                          table: 'Asset'
                        });
                      }),
                    )
                    .subscribe((res) => {
                      params.success([null, '', ...res]);
                    }),
                );
              }
            },
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === '') {
                return this.translate.instant('pages.common.ag-grid.internal.blanks');
              }
              if (params.value && typeof params.value === 'string') {
                const translationKey = `asset_form_drawer.options.${params.value}`;
                return this.translate.instant(translationKey);
              }
              return params.value;
            },
          },
          valueFormatter: (params) => {
            if (params.value && typeof params.value === 'string') {
              const translationKey = `asset_form_drawer.options.${params.value}`;
              return this.translate.instant(translationKey);
            }
            return params.value || '-';
          },
        },
        {
          field: 'maintenance_evaluation_date',
          headerName: this.translate.instant('asset_form_drawer.maintenance.evaluation_date'),
          width: 155,
          filter: 'agDateColumnFilter',
          valueFormatter: this.dateFormatter,
          hide: false,
        },
      ] : []),
      {
        field: 'investment_current_price',
        headerName: this.translate.instant("Next CAPEX"),
        hide: false,
        width: 90,
        filter: 'agNumberColumnFilter',
        valueFormatter:  this.priceFormatter,
      },
      {
        field: 'investment_final_schedule_to',
        headerName: this.translate.instant("Next CAPEX date"),
        hide: false,
        width: 120,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'investment_status',
        headerName: this.translate.instant("Next CAPEX status"),
        hide: false,
        width: 125,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'investment_budget_origin',
        headerName: this.translate.instant("Next CAPEX budget origin"),
        refData: this.budgetOriginsMap,
        hide: true,
        width: 155,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return this.budgetOriginsMap[params.value] || params.value;
          },
          refreshValuesOnOpen: false,
          suppressClearModelOnRefreshValues: true,
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') {
            return '-';
          }
          return this.budgetOriginsMap[params.value] || params.value;
        },
      },
      {
        field: 'investment_created_on',
        headerName: this.translate.instant("Next CAPEX creation date"),
        valueFormatter: this.dateFormatter,
        hide: true,
        width: 155,
        filter: 'agDateColumnFilter',
      },
      ...(customerConfig?.uniformat_code_display_in_equipment_sheet ? [{
        field: 'uniformat_ii_code',
        headerName: this.translate.instant('Code Uniformat II'),
        width: 140,
        filter: 'agSetColumnFilter',
        hide: false,
        allowNull: true,
        valueFormatter: (params) => params.value || '-',
        filterParams: {
          values: (params) => {
            if (params && params.colDef && params.colDef.field) {
              this.subscriptions.push(
                this.perimetersService.perimeters$
                  .pipe(
                    switchMap((perimeters) => {
                      const { api } = params;
                      return this.api.available_filter_values({
                        model: 'UniformatIiCode',
                        buildings: PerimetersService.getBuildingsIds(perimeters),
                        filterModel: api.getFilterModel(),
                        table: 'Asset'
                      });
                    }),
                  )
                  .subscribe((res) => {
                    params.success([null, '', ...res]);
                  }),
              );
            }
          },
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
      }] : []),
      {
        field: 'super_category',
        headerName: this.translate.instant(
          'pages.admin.nomenclature.column_super_category',
        ),
        hide: false,
        refData: this.superCategoriesMap,
        filter: 'agSetColumnFilter',
        quickFilter: true,
        width: 130
      },
      {
        field: 'category',
        headerName: this.translate.instant(
          'pages.insight.assets.column_category',
        ),
        width: 100,
        refData: this.categoriesMap,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'sub_category',
        headerName: this.translate.instant(
          'pages.insight.assets.column_sub_category',
        ),
        width: 100,
        refData: this.subCategoriesMap,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'asset_type_object',
        headerName: this.translate.instant(
          'pages.insight.assets.column_asset_type',
        ),
        width: 120,
        refData: this.assetTypesMap,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'technical_state_notation',
        headerName: this.translate.instant(
          'pages.insight.assets.column_technical_state_projection',
          { year: year ? year : '' },
        ),
        cellRenderer: 'technicalStateRenderer',
        cellClass: 'no-padding-cell',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params) => {
            params.success([null, 0, 1, 2, 3]);
          },
          refreshValuesOnOpen: false,
          suppressClearModelOnRefreshValues: true,
        },
        filterValueGetter: (params) => {
          return this.stateLevelsService.getLevelIndexFromNotation(params.data?.technical_state_notation);
        },
        refData: this.technicalEvaluationMap,
        comparator: this.technicalStateComparator,
        width: 110,
        // quickFilter: true,
        hide: true,
        cellStyle: { 'text-align': 'center' },
      },
      {
        field: 'planned_investment_number',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_planned_investment_number',
        ),
        filter: 'agNumberColumnFilter',
        hide: true,
        quickFilter: true,
        width: 120,
      },
      {
        field: 'planned_replacement_year',
        headerName: this.translate.instant('Planned replacement year'),
        width: 130,
        filter: 'agNumberColumnFilter',
        hide: true,
      },
      {
        field: 'hypothesis_replacement_year',
        headerName: this.translate.instant('Hypothesis replacement year'),
        width: 130,
        filter: 'agNumberColumnFilter',
        hide: true,
      },
      // COLUMNS WITH QUICKFILTERS
      {
        field: 'remaining_lifetime_with_impact',
        headerName: this.translate.instant(
          'pages.insight.assets.column_lifetime',
        ),
        filter: 'agNumberColumnFilter',
        hide: true,
        width: 140,
        valueFormatter: this.nullFormatter,
      },
      {
        field: 'maintenance_code',
        headerName: this.translate.instant('pages.insight.assets.column_maintenance_code'),
        width: 120,
        hide: true,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'zone_details',
        headerName: this.translate.instant('asset_form_drawer.synthese.zone_details'),
        width: 150,
        hide: true,
        filter: 'agSetColumnFilter',
        quickFilter: true,
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            // Flatten multi-line textarea values so the filter label renders as a single line.
            return String(params.value).replace(/\s+/g, ' ').trim();
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'brand',
        headerName: this.translate.instant('pages.insight.asset.brand'),
        width: 90,
        hide: true,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'model',
        headerName: this.translate.instant('pages.insight.asset.model'),
        width: 90,
        hide: true,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'power',
        headerName: this.translate.instant('pages.insight.asset.power'),
        width: 95,
        hide: true,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'refrigerant_type',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_refrigerant_type',
        ),
        hide: true,
        width: 120,
        refData: this.refrigerantTypesMap,
        filter: 'agSetColumnFilter',
        allowNull: true,
        filterParams: {
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return this.translate.instant('pages.common.ag-grid.internal.blanks');
            }
            return params.value;
          },
        },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'nb_documents',
        headerName: this.translate.instant(
          'pages.insight.assets.column_documents',
        ),
        width: 100,
        filter: 'agNumberColumnFilter',
        hide: true
      },
      ...(defectManagement ? [{
        field: 'defaults',
        headerName: this.translate.instant('Defaults'),
        filter: 'agNumberColumnFilter',
        width: 90,
        hide: true,
      }] : []),
      {
        field: 'investment_priority',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_priority',
        ),
        hide: true,
        filter: 'agTextColumnFilter',
        suppressColumnsToolPanel: true,
        width: 100,
        suppressFiltersToolPanel: true,
      },
      {
        field: 'investment_sub_category',
        headerName: this.translate.instant(
          'pages.insight.investment.investment_category',
        ),
        hide: true,
        filter: 'agTextColumnFilter',
        width: 100,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
      },
      {
        field: 'investment_period',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_period',
        ),
        hide: true,
        filter: 'agTextColumnFilter',
        suppressColumnsToolPanel: true,
        width: 100,
        suppressFiltersToolPanel: true,
      },
      {
        field: 'planned_renewal',
        headerName: '',
        hide: true,
        suppressColumnsToolPanel: true,
        width: 100,
        suppressFiltersToolPanel: true,
        refData: this.trueFalseMap,
        filterParams: {
          values: (params) => {
            params.success([true, false]);
          },
        },
      },
      {
        field: 'has_maintenance_code',
        headerName: '',
        hide: true,
        suppressColumnsToolPanel: true,
        width: 100,
        suppressFiltersToolPanel: true,
        refData: this.trueFalseMap,
        filterParams: {
          values: (params) => {
            params.success([true, false]);
          },
        },
      },
      {
        field: 'asset_id',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_asset_id',
        ),
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        lockVisible: true,
        lockPosition: true,
        filter: "agSetColumnFilter"
      },
      {
        field: 'is_vital_organ',
        headerName: this.translate.instant('Is vital organ'),
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params) =>
            params.value ? this.translate.instant('Yes') : this.translate.instant('No'),
        },
        valueFormatter: (params) =>
          params.value === true
            ? this.translate.instant('Yes')
            : this.translate.instant('No'),
        width: 120,
      },
    ];
      })
    );
  }

  public getAgGridAssetColumns(year: number, config?: { onDialogClose: (params: any) => void }, forImport?: boolean): Observable<KPI.CustomColDef[]> {
    return combineLatest([
      this.scope.perimeterTypes$.pipe(first(), idMapToArray()),
      this.scope.assetFloors$.pipe(first(), idMapToArray()),
      this.scope.superCategories$.pipe(first(), idMapToArray()),
      this.scope.categories$.pipe(first(), idMapToArray()),
      this.scope.subCategories$.pipe(first(), idMapToArray()),
      this.scope.assetTypes$.pipe(first(), idMapToArray()),
      this.scope.refrigerantTypes$.pipe(first(), idMapToArray()),
      this.autoPpaService.isAutoPpaEnabled$,
      this.scope.ownershipTypes$.pipe(first(), idMapToArray()),
      this.scope.investmentBudgetOrigins$.pipe(first(), idMapToArray()),
      this.quantityUnitsService.quantityUnits$.pipe(first()),
      this.loadSiteAndPerimeters(),
    ]).pipe(
      switchMap(
        ([
          perimeterTypes,
          assetFloors,
          superCategories,
          categories,
          subCategories,
          assetTypes,
          refrigerantTypes,
          isAutoPpaEnabled,
          ownershipTypes,
          budgetOrigins,
          units,
          _perimeters,
        ]) => {
          /**
           * Setup reference data
           */
          (perimeterTypes as KPI.PerimeterType[]).forEach((perimeterType) => {
            this.perimeterTypesMap[perimeterType.id] = perimeterType.name;
          });
          (assetFloors as KPI.AssetFloor[]).forEach((assetFloor) => {
            this.assetFloorsMap[assetFloor.id] = assetFloor.name;
          });
          (superCategories as KPI.SuperCategory[]).forEach((superCategory) => {
            this.superCategoriesMap[superCategory.id] = superCategory.name;
          });
          (categories as KPI.Category[]).forEach((category) => {
            this.categoriesMap[category.id] = category.name;
          });
          (subCategories as KPI.SubCategory[]).forEach((subCategorie) => {
            this.subCategoriesMap[subCategorie.id] = subCategorie.name;
          });
          (assetTypes as KPI.AssetType[]).forEach((assetType) => {
            this.assetTypesMap[assetType.id] = assetType.name;
          });
          (refrigerantTypes as KPI.RefrigerantType[]).forEach((refrigerantType) => {
            this.refrigerantTypesMap[refrigerantType.id] = refrigerantType.name;
          });
          (ownershipTypes as KPI.OwnershipType[]).forEach((ownershipType) => {
            this.ownershipTypeMap[ownershipType.id] = ownershipType.name;
          });
          (budgetOrigins as KPI.InvestmentBudgetOrigin[]).forEach((origin) => {
            this.budgetOriginsMap[origin.id] = origin.name;
          });
          this.quantityUnitMap = { null: units.find((u) => u.setup_reference === 'no_unit').name };
          (units as KPI.QuantityUnit[]).forEach((unit) => {
            this.quantityUnitMap[unit.name] = unit.name;
          });

          if (forImport) {
            return this.agGridAssetColumnsImport();
          }

          return this.agGridAssetColumns(year, (isAutoPpaEnabled as boolean), config).pipe(
            map((agGridAssetColumns) => {
              agGridAssetColumns.push(...this.getAgGridImportColumns());
              const columnsToHide = [
                'asset_id',
              ];

              return agGridAssetColumns.map((column: KPI.CustomColDef) => {
                const shouldForceHide = columnsToHide.some((colToHide) => column.field?.startsWith(colToHide));
                return {
                  ...column,
                  ...(shouldForceHide ? { hide: true } : {}),
                };
              });
            })
          );
        },
      ),
    );
  }

  public getAssetsData(options: any): Observable<any> {

    const optionsWithDefaultSort = {
      ...options,
      sortModel: options.sortModel && options.sortModel.length > 0
        ? options.sortModel
        : [{ sort: 'desc', colId: 'investment_current_price' }]
    };

    if (optionsWithDefaultSort.filterModel?.last_technical_evaluation?.values) {
      optionsWithDefaultSort.filterModel.last_technical_evaluation.values =
        optionsWithDefaultSort.filterModel.last_technical_evaluation.values.map(v => v !== null ? Number(v) : null);
    }
    if (optionsWithDefaultSort.filterModel?.technical_state_notation?.values) {
      optionsWithDefaultSort.filterModel.technical_state_notation.values =
        optionsWithDefaultSort.filterModel.technical_state_notation.values.map(v => v !== null ? Number(v) : null);
    }
    if (optionsWithDefaultSort.filterModel?.investment_budget_origin?.values) {
      optionsWithDefaultSort.filterModel.investment_budget_origin.values =
        optionsWithDefaultSort.filterModel.investment_budget_origin.values.map(v => v !== null ? Number(v) : null);
    }

    return combineLatest([
      this.api.assets_table(optionsWithDefaultSort),
      this.scope.referenceFields$.pipe(first(), idMapToArray()),
      this.referenceDataService.perimeterReferenceDataMaps$.pipe(first()),
      this.scopeService.auditQuestions$.pipe(first(), idMapToArray()),
    ]).pipe(
      tap(() => this.currentAssetTableOptions.next(optionsWithDefaultSort)),
      map(([data, referenceFields, perimeters, auditQuestions]) => ({
        ...data,
        rowData: data.rowData.map((row) => {
          const last_notation = row.asset_notation?.map((n) => {
            let auditQuestion: KPI.AuditQuestion;
            let auditQuestionItem: KPI.AuditQuestionItem;
            for (auditQuestion of auditQuestions) {
              auditQuestionItem = auditQuestion.items.find((item) => item.id === n.question_item);
              if (auditQuestionItem) {
                break;
              }
            }
            return {
              ...n,
              audit_question_object: auditQuestion
            }
          })?.filter((n) => n.audit_question_object.mnemonic === 'technical_state')?.at(-1);
          const site = perimeters.find((p) => p.id === row.site_name);
          if (!!site?.ref_data_map) {
            referenceFields.map((referenceField) => {
              if (
                referenceField.displayed_intelligence &&
                site.ref_data_map[referenceField.var_name]
              ) {
                row[`reference_field_${referenceField.id}`] =
                  site.ref_data_map[referenceField.var_name].value;
              }
            });
          }
          return {
            ...row,
            asset_id: row.asset_id ?? row.id,
            investment_label:
              row.investments && row.investments.length
                ? row.investments[0].label
                : '',
            price_sheets_count: row?.price_sheets_count ? row.price_sheets_count : '',
            investment_status: row?.investment_status ? row.investment_status : '',
            investment_final_schedule_to: row?.investment_final_schedule_to ? row.investment_final_schedule_to : '',
            investment_budget_origin: row?.investment_budget_origin ?? null,
            investment_created_on: row?.investment_created_on ? row.investment_created_on : '',
            theoretical_lifetime: row?.theoretical_lifetime ? row.theoretical_lifetime : '',
            investment_priority:
              row.investments && row.investments.length
                ? [
                  ...new Set(
                    row.investments
                      .filter((inv) => inv.priority)
                      .map((inv) => inv.priority),
                  ),
                ].join(', ')
                : '',
            last_technical_notation: last_notation?.note ? last_notation?.note : null,
            investment_sub_category:
              row.investments && row.investments.length
                ? [
                  ...new Set(
                    row.investments
                      .filter((inv) => inv.sub_category)
                      .map((inv) => inv.sub_category),
                  ),
                ].join(', ')
                : '',
            investment_period:
              row.investments && row.investments.length
                ? [...new Set(row.investments.map((inv) => inv.period))].join(
                  ', ',
                )
                : '',
            last_audit_date: row.last_audit_date,
          };
        }),
      })),
      shareReplay(1),
    );
  }

  public preExportAssets(columnDefs: ColDef<any, any>[]) {
    return this.api.pre_download_assets_excel_export({ columnDefs, payload: this.currentAssetTableOptions.value })
  }

  public exportAssets(columnDefs: ColDef<any, any>[]) {
    return this.exportsService.download_assets_excel_export({ columnDefs, payload: this.currentAssetTableOptions.value })
  }

  public openPriceSheets(assetTypeId: number): Observable<any> {
    return combineLatest([
      this.assetTypesService.mappedAssetTypes$,
      this.priceSheetService.getPriceSheetsByAssetType(assetTypeId),
    ]).pipe(
      first(),
      switchMap(([assetTypes, priceSheets]) => {
        const assetType = assetTypes.find((a) => a.id === assetTypeId);
        const dialogRef = this.dialogService.open(
          PriceSheetsListDialogComponent,
          {
            autoFocus: false,
            context: {
              assetType,
              priceSheets,
            }
          }
        );
        return dialogRef.onClose;
      })
    );
  }

  // Columns shown for the asset AI import. For now, this feature only works with fake data, so
  // it shows a lighter version of the asset table.
  private agGridAssetColumnsImport(): Observable<KPI.CustomColDef[]> {
    return of([
      {
        field: 'asset_label',
        headerName: this.translate.instant('pages.insight.assets.column_label'),
        pinned: false,
        width: 280,
      },
      {
        field: 'perimeter',
        headerName: this.translate.instant('Perimeter name'),
        refData: this.perimetersMap,
        filter: 'agSetColumnFilter',
        width: 220,
        hide: false,
      },
      {
        field: 'super_category',
        headerName: this.translate.instant(
          'pages.admin.nomenclature.column_super_category',
        ),
        hide: false,
        refData: this.superCategoriesMap,
        quickFilter: true,
        width: 200
      },
      {
        field: 'category',
        headerName: this.translate.instant(
          'pages.insight.assets.column_category',
        ),
        width: 230,
        refData: this.categoriesMap,
      },
      {
        field: 'sub_category',
        headerName: this.translate.instant(
          'pages.insight.assets.column_sub_category',
        ),
        width: 230,
        refData: this.subCategoriesMap,
      },
      {
        field: 'asset_type_object',
        headerName: this.translate.instant(
          'pages.insight.assets.column_asset_type',
        ),
        width: 230,
        refData: this.assetTypesMap,
      },
      {
        field: 'missing_data_count',
        headerName: this.translate.instant('Missing data'),
        width: 110,
        filter: 'agNumberColumnFilter',
        hide: true,
        valueFormatter: (params) => {
          const value = params.value;
          return value === 0 || value === null || value === undefined ? '-' : value;
        },
        cellStyle: { 'font-weight': 'normal' },
      },
      {
        field: 'asset_id',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_asset_id',
        ),
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        lockVisible: true,
        lockPosition: true,
        filter: "agSetColumnFilter"
      },
    ]);
  }
}
